/**
 * Parametricke filtry ve vyloze — cteni facetu a prace s tvarem v URL.
 *
 * PROC VLASTNI MODUL A NE `api.ts`: klient katalogu je sdileny se zbytkem
 * vylohy, kdezto tohle je jedina obrazovka. Format `parametry` v URL je
 * navic vec teto casti a patri k ni.
 *
 * TVAR V URL: `?parametry=prichut:kureci,rybi;vek:stene`
 *   - carka mezi hodnotami TEHOZ parametru znamena NEBO,
 *   - stredník mezi parametry znamena A ZAROVEN.
 * Presne tak to cte i server (`shop/catalog.ts`, `parseParameterSelection`),
 * takze adresu jde poslat a funguje i zpetne tlacitko.
 */

export interface FacetValue {
  readonly slug: string;
  readonly label: string;
  /**
   * Kolik zbozi zbyde, kdyz bude tahle hodnota ZASKRTNUTA — vcetne toho, co
   * uz je zaskrtnute v tomtez parametru (hodnoty se scitaji, viz `NEBO` vys)
   * a se vsemi ostatnimi filtry.
   *
   * Je to slib: po kliknuti na NEZASKRTNUTOU hodnotu musi vypis ukazat
   * tentyz pocet. Server ho pocita presne tou operaci, kterou dela
   * `toggleFacetValue` niz — sjednocenim. U UZ ZASKRTNUTE hodnoty je to
   * velikost soucasneho vysledku (zaskrtnuta uz je), takze pri dvou
   * zaskrtnutych hodnotach tehoz parametru maji obe stejne cislo.
   */
  readonly count: number;
  readonly selected: boolean;
}

export interface Facet {
  readonly slug: string;
  readonly name: string;
  /** Kolik polozek vysledku tenhle parametr vubec ma. Skoro nikdy vsechny. */
  readonly covered: number;
  readonly values: readonly FacetValue[];
}

/** Vybrane hodnoty podle parametru. */
export type FacetSelection = ReadonlyMap<string, readonly string[]>;

/** `prichut:kureci,rybi;vek:stene` → mapa. Neznamy tvar se ticho zahodi. */
export function parseFacetSelection(raw: string): FacetSelection {
  const selection = new Map<string, string[]>();
  if (raw.trim() === '') return selection;
  for (const group of raw.split(';')) {
    const colon = group.indexOf(':');
    if (colon <= 0) continue;
    const name = group.slice(0, colon).trim();
    const values = group
      .slice(colon + 1)
      .split(',')
      .map((value) => value.trim())
      .filter((value) => value !== '');
    if (name === '' || values.length === 0) continue;
    const existing = selection.get(name) ?? [];
    selection.set(name, [...existing, ...values.filter((value) => !existing.includes(value))]);
  }
  return selection;
}

/** Mapa → `prichut:kureci,rybi;vek:stene`. Prazdna mapa da prazdny retezec. */
export function formatFacetSelection(selection: FacetSelection): string {
  const groups: string[] = [];
  for (const [name, values] of selection) {
    if (values.length > 0) groups.push(`${name}:${values.join(',')}`);
  }
  return groups.join(';');
}

/**
 * Prepne jednu hodnotu. Zaskrtnuta se odskrtne, posledni odskrtnuta hodnota
 * parametr z adresy uplne odstrani — jinak by v URL zustal `prichut:`, ktery
 * uz nic neznamena.
 *
 * NEZASKRTNUTA SE PRIDA K OSTATNIM V TOMTEZ PARAMETRU (sjednoceni), skupina
 * se NENAHRAZUJE. Presne tuhle operaci pocitaji i pocty u hodnot
 * (`shop/catalog.ts`, `listFacets`) — kdyby se tohle zmenilo na nahrazeni,
 * zacnou cisla v chipech lhat.
 */
export function toggleFacetValue(
  selection: FacetSelection,
  parameter: string,
  value: string,
): FacetSelection {
  const next = new Map(selection);
  const current = next.get(parameter) ?? [];
  const without = current.filter((item) => item !== value);
  if (without.length === current.length) next.set(parameter, [...current, value]);
  else if (without.length === 0) next.delete(parameter);
  else next.set(parameter, without);
  return next;
}

/**
 * Nacte facety pro tentyz vyber, jaky ma vypis.
 *
 * Zamerne BEZ `limit`/`offset`/`razeni`: facety na strankovani nezavisi,
 * takze se pri „Zobrazit další" nemusi pocitat znovu.
 *
 * Chyba se NEVYHAZUJE — vraci prazdny seznam. Filtry jsou doplnek katalogu;
 * kdyz se nenactou, ma zakaznik porad mrizku se zbozim, a chybova hlaska
 * misto ni by mu ji vzala.
 */
export async function loadFacets(query: {
  search?: string;
  category?: string;
  inStock?: boolean;
  brand?: string;
  parametry?: string;
}): Promise<readonly Facet[]> {
  const params = new URLSearchParams();
  if (query.search !== undefined && query.search !== '') params.set('search', query.search);
  if (query.category !== undefined && query.category !== '') params.set('category', query.category);
  if (query.inStock === true) params.set('inStock', 'true');
  if (query.brand !== undefined && query.brand !== '') params.set('brand', query.brand);
  if (query.parametry !== undefined && query.parametry !== '') {
    params.set('parametry', query.parametry);
  }
  const search = params.toString();

  try {
    const response = await fetch(`/api/shop/facets${search === '' ? '' : `?${search}`}`, {
      headers: { 'x-requested-with': 'zooshopik-admin' },
    });
    if (!response.ok) return [];
    const body = (await response.json()) as { facets?: Facet[] };
    return body.facets ?? [];
  } catch {
    return [];
  }
}
